import {
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  Link,
} from '@tanstack/react-router'
import { Loader } from '../../shared/components'
import {
  LazyDashboardPage,
  LazySignInPage,
  LazySignUpPage,
  LazyForgotPasswordPage,
  LazyConfirmEmailPage,
  LazyLinkExpiredPage,
  LazyResetPasswordPage,
  LazyRoleSelectionPage,
  LazyOrganizationSetupPage,
  LazyPackageSelectionPage,
  LazyFinalizePage,
  LazyProfilePage,
  LazyChangePasswordPage,
  LazyHousesPage,
  LazyHouseManagersPage,
  LazyResidentsPage,
  LazyArchivedResidentsPage,
  LazyFinancialsPage,
  LazyAnnouncementsPage,
  LazyResourcesPage,
  LazySettingsPage,
  LazyHousesManagementOverviewPage,
  LazyHouseDetailsPage,
  LazyEditHousePage,
  LazyAllResourcesPage,
  LazyResourceDetailsPage,
  LazyAllAnnoucementPage,
  LazyAnnoucementDetailPage,
  LazyFinancialHistoryPage,
  LazyResidentFinancialHistoryPage,
  LazyFinancialReportPage,
  LazyHouseManagerDetailsPage,
  LazyHouseResidentsPage,
  LazyHouseResidentDetailsPage,
  LazyEditHouseResidentPage,
  LazyResidentOnboardingHousePage,
  LazyResidentOnboardingHousesDetailsPage,
  LazyResidentOnboardingHousePaymentPage,
  LazyResidentDashboardHomePage,
  LazyResidentExploreHousesPage,
  LazyResidentExploreHousesDetailsPage,
  LazyResidentPaymentsPage,
  LazyResidentPaymentsHistoryPage,
  LazyResidentPaymentDetailsPage,
  LazyResidentAnnouncementsPage,
  LazyResidentAnnoucementsPage,
  LazyResidentAnnoucementDetailPage,
  LazyResidentResourcesPage,
  LazyResidentResourceDetailsPage,
  LazyStripeConnectCallbackPage,
  LazyManagerNoOrganizationPage,
  LazyResidentOnboardingPaymentSuccessPage,
  LazyInvitationLandingPage,
  LazySubscriptionExpiredPage,
} from './LazyComponents'
import { FeatureSuspense } from './SuspenseWrapper'
import { NotFoundPage } from '../../shared/pages'
import { authApi } from '../../features/auth/api/authApi'

import { RootLayout } from './RootLayout'
import Home from '@/pages/Home'
import Features from '@/pages/Features'
import About from '@/pages/About'
import ContactUs from '@/pages/ContactUs'

// Auth guard function
const requireAuth = () => {
  const token = localStorage.getItem('access_token')
  const user = authApi.getStoredUser()

  if (!token || !user) {
    throw redirect({
      to: '/sign-in',
    })
  }

  // If user hasn't completed role selection, force onboarding except on onboarding routes
  const path = window.location.pathname
  if (user.role === 'PENDING' && !path.startsWith('/onboarding')) {
    throw redirect({ to: '/onboarding/role' })
  }

  return { user }
}

// Guest guard function (redirect if authenticated)
const requireGuest = () => {
  const token = localStorage.getItem('access_token')
  const user = authApi.getStoredUser()

  if (token && user) {
    throw redirect({
      to: '/',
    })
  }
}

// Role guard function
const requireRole = (allowedRoles: string[]) => {
  const { user } = requireAuth()

  if (!allowedRoles.includes(user.role)) {
    throw redirect({
      to: '/unauthorized',
    })
  }

  return { user }
}

const rootRoute = createRootRoute({
  component: RootLayout,
})

// Public routes (no authentication required)
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  component: () => (
    <Home />
  ),
})

const featuresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/features',
  component: () => (
    <Features />
  ),
})

const aboutUsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about-us',
  component: () => (
    <About />
  ),
})

const contactUsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contact-us',
  component: () => (
    <ContactUs />
  ),
})

// Auth routes (guest only)
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  beforeLoad: requireGuest,
  component: () => (
    <FeatureSuspense featureName="Sign In">
      <LazySignInPage />
    </FeatureSuspense>
  ),
})

const subscriptionExpiredRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'subscription/expired',
  component: () => (
    <FeatureSuspense featureName="Subscription Page">
      <LazySubscriptionExpiredPage />
    </FeatureSuspense>
  ),
  beforeLoad: requireAuth,
})

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  beforeLoad: requireGuest,
  component: () => (
    <FeatureSuspense featureName="Sign Up">
      <LazySignUpPage />
    </FeatureSuspense>
  ),
})

// Email/Password utility routes
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  beforeLoad: requireGuest,
  component: () => (
    <FeatureSuspense featureName="Password Recovery">
      <LazyForgotPasswordPage />
    </FeatureSuspense>
  ),
})

const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirm-email',
  component: () => (
    <FeatureSuspense featureName="Email Confirmation">
      <LazyConfirmEmailPage />
    </FeatureSuspense>
  ),
})

const linkExpiredRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/link-expired',
  component: () => (
    <FeatureSuspense featureName="Link Expired">
      <LazyLinkExpiredPage />
    </FeatureSuspense>
  ),
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: () => (
    <FeatureSuspense featureName="Reset Password">
      <LazyResetPasswordPage />
    </FeatureSuspense>
  ),
});

// Invitation route (public, no auth required)
const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || '',
  }),
  component: () => (
    <FeatureSuspense featureName="Manager Invitation">
      <LazyInvitationLandingPage />
    </FeatureSuspense>
  ),
});

// Onboarding routes
const roleSelectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/role',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Role Selection">
      <LazyRoleSelectionPage />
    </FeatureSuspense>
  ),
})

const packageSelectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/packages',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Package Selection">
      <LazyPackageSelectionPage />
    </FeatureSuspense>
  ),
})

const organizationSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/organization',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Organization Setup">
      <LazyOrganizationSetupPage />
    </FeatureSuspense>
  ),
})

const finalizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/finalize',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Finalize Setup">
      <LazyFinalizePage />
    </FeatureSuspense>
  ),
})

// Profile routes
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Profile">
      <LazyProfilePage />
    </FeatureSuspense>
  ),
})

const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/change-password',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Change Password">
      <LazyChangePasswordPage />
    </FeatureSuspense>
  ),
})

// Protected routes (authentication required)
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    const { user } = requireAuth()
    
    // Redirect residents to their dashboard
    if (user.role === 'RESIDENT') {
      throw redirect({
        to: '/resident/dashboard',
      })
    }
    
    return { user }
  },
  component: () => (
    <FeatureSuspense featureName="Dashboard">
      <LazyDashboardPage />
    </FeatureSuspense>
  ),
})

// Resident dashboard route (resident role required)
const residentOnboardingHouses = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/onboarding/houses',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Onboarding Featured Houses">
      <LazyResidentOnboardingHousePage />
    </FeatureSuspense>
  ),
})

const residentOnboardingHouseDetails = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/onboarding/houses/$houseId',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Onboarding Featured Houses">
      <LazyResidentOnboardingHousesDetailsPage />
    </FeatureSuspense>
  ),
})

const residentOnboardingHousePayment = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/onboarding/houses/$houseId/payment',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Onboarding Featured Houses">
      <LazyResidentOnboardingHousePaymentPage />
    </FeatureSuspense>
  ),
})

const residentOnboardingPaymentSuccess = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/onboarding/houses/payment-success',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Onboarding Payment Success">
      <LazyResidentOnboardingPaymentSuccessPage />
    </FeatureSuspense>
  ),
})

const residentDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Dashboard">
      <LazyResidentDashboardHomePage />
    </FeatureSuspense>
  ),
})

const residentDashboardExploreHousesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/houses',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Explore Houses">
      <LazyResidentExploreHousesPage />
    </FeatureSuspense>
  ),
})

const residentDashboardExploreHousesDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/houses/$houseId',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Explore Houses Details">
      <LazyResidentExploreHousesDetailsPage />
    </FeatureSuspense>
  ),
})

const residentDashboardPaymentsPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/payments',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Payments Overview">
      <LazyResidentPaymentsPage />
    </FeatureSuspense>
  ),
})

const residentDashboardPaymentsHistoryPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/payments/history',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Payments History">
      <LazyResidentPaymentsHistoryPage />
    </FeatureSuspense>
  ),
})

const residentDashboardPaymentsHistoryDetailsPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/payments/history/$invoiceId',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Payments Details">
      <LazyResidentPaymentDetailsPage />
    </FeatureSuspense>
  ),
})

const stripeConnectCallbackPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stripe/callback',
  validateSearch: (search: Record<string, string>) => ({
    code: search.code ?? '',
    state: search.state ?? '',
  }),
  beforeLoad: () => requireRole(['OWNER', 'ADMIN', 'MANAGER']),
  component: () => (
    <FeatureSuspense featureName="Stripe oAuth Callback">
      <LazyStripeConnectCallbackPage />
    </FeatureSuspense>
  ),
})

const residentDashboardAnnouncementsPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/announcements',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Dashboard Announcement">
      <LazyResidentAnnouncementsPage />
    </FeatureSuspense>
  ),
})

const residentDashboardAllAnnouncementsPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/announcements/all',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident's All Announcement">
      <LazyResidentAnnoucementsPage />
    </FeatureSuspense>
  ),
})

const residentDashboardAnnouncementDetailsPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/announcements/$announcementId',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Dashboard Announcement Details">
      <LazyResidentAnnoucementDetailPage />
    </FeatureSuspense>
  ),
})

const residentDashboardResourcesPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/resources',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Dashboard Resources">
      <LazyResidentResourcesPage />
    </FeatureSuspense>
  ),
})

const residentDashboardResourceDetailsPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resident/dashboard/resources/$resourceId',
  beforeLoad: () => requireRole(['RESIDENT']),
  component: () => (
    <FeatureSuspense featureName="Resident Dashboard Resources Details">
      <LazyResidentResourceDetailsPage />
    </FeatureSuspense>
  ),
})


// Admin routes (admin role required)
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  beforeLoad: () => requireRole(['ADMIN']),
  component: () => (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-text-primary mb-8">
          Admin Dashboard
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-2">User Management</h3>
            <p className="text-text-secondary">Manage system users and roles</p>
          </div>
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-2">System Settings</h3>
            <p className="text-text-secondary">Configure system preferences</p>
          </div>
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-2">Reports</h3>
            <p className="text-text-secondary">View system reports and analytics</p>
          </div>
        </div>
      </div>
    </div>
  ),
})

// Manager routes (admin or manager role required)
const managerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/manager',
  beforeLoad: () => requireRole(['ADMIN', 'MANAGER']),
  component: () => (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-text-primary mb-8">
          Manager Dashboard
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-2">House Management</h3>
            <p className="text-text-secondary">Manage houses and facilities</p>
          </div>
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-2">Resident Management</h3>
            <p className="text-text-secondary">Manage residents and their information</p>
          </div>
        </div>
      </div>
    </div>
  ),
})

// Houses routes
const housesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/houses',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Houses">
      <LazyHousesPage />
    </FeatureSuspense>
  ),
})

const housesManagementOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/houses/overview',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Houses Management Overview">
      <LazyHousesManagementOverviewPage />
    </FeatureSuspense>
  ),
})

// House detail route with parameter
const houseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/houses/$houseId',
  beforeLoad: requireAuth,
 component: () => (
    <FeatureSuspense featureName="Houses">
      <LazyHouseDetailsPage />
    </FeatureSuspense>
  ),
})

// House detail route with parameter
const houseUpdateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/houses/edit/$houseId',
  beforeLoad: requireAuth,
 component: () => (
    <FeatureSuspense featureName="Houses">
      <LazyEditHousePage />
    </FeatureSuspense>
  ),
})

const houseManagersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/houses/managers',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="House Managers">
      <LazyHouseManagersPage />
    </FeatureSuspense>
  ),
})

const houseManagersDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/houses/managers/$managerId',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="House Manager Details">
      <LazyHouseManagerDetailsPage />
    </FeatureSuspense>
  ),
})

const houseResidentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/houses/residents',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="House Residents">
      <LazyHouseResidentsPage />
    </FeatureSuspense>
  ),
})

const houseResidentDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/houses/residents/$residentId',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="House Resident Details">
      <LazyHouseResidentDetailsPage />
    </FeatureSuspense>
  ),
})

const editHouseResidentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/houses/residents/edit/$residentId',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="House Resident Details">
      <LazyEditHouseResidentPage />
    </FeatureSuspense>
  ),
})

// Residents routes
const residentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/residents',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Residents">
      <LazyResidentsPage />
    </FeatureSuspense>
  ),
})

// Resident detail route with parameter
const residentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/residents/$residentId',
  beforeLoad: requireAuth,
  component: () => (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Resident Details</h1>
        <p className="text-gray-600">
          View and manage resident information
        </p>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Resident Profile</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Detailed resident information, payment history, and case management will be available here.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Coming Soon:</strong> Resident profile, payment tracking, and case management features.
            </p>
          </div>
        </div>
      </div>
    </div>
  ),
})

const archivedResidentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/residents/archive',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Archived Residents">
      <LazyArchivedResidentsPage />
    </FeatureSuspense>
  ),
})

// Financials routes (Owner-only)
const financialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/financials',
  beforeLoad: () => requireRole(['OWNER', 'ADMIN']),
  component: () => (
    <FeatureSuspense featureName="Financials">
      <LazyFinancialsPage />
    </FeatureSuspense>
  ),
})

const financialsHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/financials/history',
  beforeLoad: () => requireRole(['OWNER', 'ADMIN']),
  component: () => (
    <FeatureSuspense featureName="Financial History">
      <LazyFinancialHistoryPage />
    </FeatureSuspense>
  ),
})
const ResidentFinancialHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/financials/history/resident/$residentId',
  beforeLoad: () => requireRole(['OWNER', 'ADMIN']),
  component: () => (
    <FeatureSuspense featureName="Financial Resident History">
      <LazyResidentFinancialHistoryPage />
    </FeatureSuspense>
  ),
})

const FinancialReportPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/financials/$reportType/$reportId',
  beforeLoad: () => requireRole(['OWNER', 'ADMIN']),
  component: () => (
    <FeatureSuspense featureName="Financial Report Page">
      <LazyFinancialReportPage />
    </FeatureSuspense>
  ),
})

// Announcements routes
const announcementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/announcements',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Announcements">
      <LazyAnnouncementsPage />
    </FeatureSuspense>
  ),
})

const allAnnouncementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/announcements/all',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="All Announcements">
      <LazyAllAnnoucementPage />
    </FeatureSuspense>
  ),
})

const annoucementDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/announcements/$announcementId',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Announcement Detail">
      <LazyAnnoucementDetailPage />
    </FeatureSuspense>
  ),
})

// Resources routes
const resourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Resources">
      <LazyResourcesPage />
    </FeatureSuspense>
  ),
})

const allResourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources/all',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="All Resources">
      <LazyAllResourcesPage />
    </FeatureSuspense>
  ),
})

const resourceDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources/$resourceId',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Resources Details">
      <LazyResourceDetailsPage />
    </FeatureSuspense>
  ),
})

// Settings routes
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="Settings">
      <LazySettingsPage />
    </FeatureSuspense>
  ),
})

// Manager no organization route
const managerNoOrganizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/no-organization',
  beforeLoad: requireAuth,
  component: () => (
    <FeatureSuspense featureName="No Organization">
      <LazyManagerNoOrganizationPage />
    </FeatureSuspense>
  ),
})

// Unauthorized route
const unauthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/unauthorized',
  component: () => (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-red-600 mb-4">403</h1>
        <h2 className="text-2xl font-semibold text-text-primary mb-2">Access Denied</h2>
        <p className="text-text-secondary mb-8">
          You don't have permission to access this resource.
        </p>
        <Link to="/" className="btn-primary">Go to Dashboard</Link>
      </div>
    </div>
  ),
})

// 404 route
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: NotFoundPage,
})

// Create the route tree
const routeTree = rootRoute.addChildren([
  homeRoute,
  featuresRoute,
  aboutUsRoute,
  contactUsRoute,
  dashboardRoute,
  signInRoute,
  signUpRoute,
  forgotPasswordRoute,
  confirmEmailRoute,
  linkExpiredRoute,
  resetPasswordRoute,
  invitationRoute,
  roleSelectionRoute,
  packageSelectionRoute,
  subscriptionExpiredRoute,
  organizationSetupRoute,
  finalizeRoute,
  financialsHistoryRoute,
  ResidentFinancialHistoryRoute,
  FinancialReportPageRoute,
  profileRoute,
  changePasswordRoute,
  housesRoute,
  housesManagementOverviewRoute,
  houseDetailRoute,
  houseUpdateRoute,
  houseManagersRoute,
  houseManagersDetailsRoute,
  houseResidentsRoute,
  houseResidentDetailsRoute,
  editHouseResidentRoute,
  residentsRoute,
  residentDetailRoute,
  archivedResidentsRoute,
  financialsRoute,
  announcementsRoute,
  allAnnouncementsRoute,
  annoucementDetailRoute,
  resourcesRoute,
  allResourcesRoute,
  resourceDetailsRoute,
  settingsRoute,
  adminRoute,
  managerRoute,
  unauthorizedRoute,
  notFoundRoute,
  residentDashboardRoute,
  residentOnboardingHouses,
  residentOnboardingHouseDetails,
  residentOnboardingHousePayment,
  residentOnboardingPaymentSuccess,
  residentDashboardExploreHousesRoute,
  residentDashboardExploreHousesDetailsRoute,
  residentDashboardPaymentsPageRoute,
  residentDashboardPaymentsHistoryPageRoute,
  residentDashboardPaymentsHistoryDetailsPageRoute,
  stripeConnectCallbackPageRoute,
  residentDashboardAnnouncementsPageRoute,
  residentDashboardAllAnnouncementsPageRoute,
  residentDashboardAnnouncementDetailsPageRoute,
  residentDashboardResourcesPageRoute,
  residentDashboardResourceDetailsPageRoute,
  managerNoOrganizationRoute,
])

// Create and export the router
export const router = createRouter({
  routeTree,
  defaultPendingComponent: () => (
    <Loader fullScreen text="Loading page..." />
  ),
  defaultErrorComponent: ({ error }) => (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-2">Something went wrong!</h1>
        <p className="text-text-secondary">{error?.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary mt-4"
        >
          Reload Page
        </button>
      </div>
    </div>
  ),
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}